import {
    CompoundTranslationType,
    CompoundTranslationTypeGroupKey,
    ScreeningFlag,
    ScreeningLocationDetailView,
    ScreeningLocationResultItem,
    ScreeningPolygonStatus
} from 'src/app/contracts';
import { format } from 'date-fns';
import { Injectable } from '@angular/core';
import { LocationDetailsHelper } from './location-details.helper';

@Injectable()
export class ScreeningLocationCSVHelper {
    public flags: ScreeningFlag = 0;

    constructor(
        private readonly locationDetailsHelper: LocationDetailsHelper
    ) {

    }

    public createData(
        locationNumber: string, locationName: string, pollutionStatusCode: string,
        screeningLocationDetails: ScreeningLocationDetailView,
        showV2: boolean, v2ScreeningResults: { [key: number]: Array<ScreeningLocationResultItem> },
        showV1: boolean, v1ScreeningResults: { [key: number]: Array<ScreeningLocationResultItem> }
    ): any {
        const fieldDefs = [];
        let data: any = {};
        let ret = '\uFEFF'; // This is for BOM

        fieldDefs.push({
            label: 'Header',
            value: 'headerResultater'
        });
        data.headerResultater = 'GrundRisk - Resultater;' + format(new Date(), 'dd/MM/yyyy');
        ret += data.headerResultater + '\n';

        ret += 'Danmarks Miljøportal\n';

        fieldDefs.push({
            label: 'Lokalitet',
            value: 'location'
        });
        data.location = locationNumber + ' - ' + locationName;
        ret += 'Lokalitet;'
            + data.location + '\n';

        if (showV2) {
            const factor = this.locationDetailsHelper
                .getNumberFormatWithDigitsInfo(screeningLocationDetails.v2PolygonResult.factor, '1.0-1');

            fieldDefs.push({
                label: 'V2 kortlagt lokalitet',
                value: 'showV2Header'
            });
            const locationType = this.locationDetailsHelper.getLeftBoxTitle(pollutionStatusCode);
            ret += `${locationType}\n`;

            fieldDefs.push({
                label: 'Maksimal overskridelse',
                value: 'screeningFactorText'
            });
            data.screeningFactorText = factor;
            ret += 'Maksimal overskridelse;' + data.screeningFactorText + '\n';

            fieldDefs.push({
                label: 'Risikovurdering',
                value: 'screeningPolygonStatusText'
            });
            if (screeningLocationDetails.v2PolygonResult.polygonStatus === ScreeningPolygonStatus.NoRisk) {
                data.screeningPolygonStatusText = 'Ingen risiko';
            }
            else if (screeningLocationDetails.v2PolygonResult.polygonStatus === ScreeningPolygonStatus.Risk) {
                data.screeningPolygonStatusText = 'Risiko';
            }
            else {
                data.screeningPolygonStatusText = 'Olie- og benzinprodukter';
            }

            ret += 'Risikovurdering;' + data.screeningPolygonStatusText + '\n';

            if (screeningLocationDetails.v2PolygonResult.reassessment) {
                fieldDefs.push({
                    label: 'Revurderet af',
                    value: 'screeningreassessmentV2'
                });

                data.screeningreassessmentV2 = '';
                if (screeningLocationDetails.v2PolygonResult.reassessment.caseWorker) {
                    data.screeningreassessmentV2 = screeningLocationDetails.v2PolygonResult.reassessment.caseWorker.name;
                }
                data.screeningreassessmentV2 = data.screeningreassessmentV2
                    + ';' + screeningLocationDetails.v2PolygonResult.reassessment.comment;
                ret += 'Revurderet af;' + data.screeningreassessmentV2 + '\n';
            }

            const first = screeningLocationDetails.v2Screenings[0];
            if (first) {
                fieldDefs.push({
                    label: 'Dato',
                    value: 'screeningDatoText'
                });
                data.screeningDatoText = this.locationDetailsHelper.getDateFormat(first.createdAt);
                ret += 'Dato;' + data.screeningDatoText + '\n';
            }
        }

        if (showV1) {
            const factor = this.locationDetailsHelper
                .getNumberFormatWithDigitsInfo(screeningLocationDetails.v1PolygonResult.factor, '1.0-1');
            fieldDefs.push({
                label: 'V1 kortlagt lokalitet',
                value: 'showV2Header'
            });
            data.showV1Header = '';
            const locationType = this.locationDetailsHelper.getLeftBoxTitle(pollutionStatusCode);
            ret += `${locationType}\n`;

            fieldDefs.push({
                label: 'Maksimal overskridelse',
                value: 'screeningFactorTextV1'
            });
            data.screeningFactorTextV1 = factor;
            ret += 'Maksimal overskridelse;' + data.screeningFactorTextV1 + '\n';

            fieldDefs.push({
                label: 'Risikovurdering',
                value: 'screeningPolygonStatusTextV1'
            });
            if (screeningLocationDetails.v1PolygonResult.polygonStatus === ScreeningPolygonStatus.NoRisk) {
                data.screeningPolygonStatusTextV1 = 'Ingen risiko';
            }
            else if (screeningLocationDetails.v1PolygonResult.polygonStatus === ScreeningPolygonStatus.Risk) {
                data.screeningPolygonStatusTextV1 = 'Risiko';
            }
            else {
                data.screeningPolygonStatusTextV1 = 'Olie- og benzinprodukter';
            }

            ret += 'Risikovurdering;' + data.screeningPolygonStatusTextV1 + '\n';

            if (screeningLocationDetails.v1PolygonResult.reassessment) {
                fieldDefs.push({
                    label: 'Revurderet af',
                    value: 'screeningreassessmentV1'
                });

                data.screeningreassessmentV1 = '';
                if (screeningLocationDetails.v1PolygonResult.reassessment.caseWorker) {
                    data.screeningreassessmentV1 = screeningLocationDetails.v1PolygonResult.reassessment.caseWorker.name;
                }
                data.screeningreassessmentV1 = data.screeningreassessmentV1
                    + ';' + screeningLocationDetails.v1PolygonResult.reassessment.comment;
                ret += 'Revurderet af;' + data.screeningreassessmentV1 + '\n';
            }

            const first = screeningLocationDetails.v1Screenings[0];
            if (first) {
                fieldDefs.push({
                    label: 'Dato',
                    value: 'screeningDatoText'
                });
                data.screeningDatoText = this.locationDetailsHelper.getDateFormat(first.createdAt);
                ret += 'Dato;' + data.screeningDatoText + '\n';
            }
        }

        // add flags
        fieldDefs.push({
            label: 'Flags',
            value: 'flagText'
        });
        ret += 'Flags:';
        let flags = ScreeningFlag.None;
        const allScreeningResults = screeningLocationDetails.v1Screenings.concat(screeningLocationDetails.v2Screenings);
        for (const screeningResult of allScreeningResults) {
            // tslint:disable-next-line: no-bitwise
            flags = flags | screeningResult.flag;
        }

        if (flags === ScreeningFlag.None) {
            ret += ';' + 'Ingen flag på denne lokalitet\n';
        }

        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Bly_kobber_eller_PAH) === ScreeningFlag.Bly_kobber_eller_PAH) {
            ret += ';' + '* Bly, kobber eller PAH fjernet i trin 1\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Kompleks_geologi) === ScreeningFlag.Kompleks_geologi) {
            ret += ';' + '* Område med kompleks geologi\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Manglende_modelstof) === ScreeningFlag.Manglende_modelstof) {
            ret += ';' + '* Et eller flere forureningsstoffer mangler et modelstof\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Svag_geologi) === ScreeningFlag.Svag_geologi) {
            ret += ';' + '* Svag datadækning for bestemmelse af dæklagstykkelser\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.MTBE_fjernet) === ScreeningFlag.MTBE_fjernet) {
            ret += ';' + '* MTBE fjernet\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Boring_uden_ler) === ScreeningFlag.Boring_uden_ler) {
            ret += ';' + '* Lokalitet har boring uden ler i en radius af 100-300 m fra kant\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Risiko_pga_vandindvinding) === ScreeningFlag.Risiko_pga_vandindvinding) {
            ret += ';' + '* Lokalitet udgør en risiko fordi der ligger en vandindvindingsboring indenfor 100 m\n';
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Kun_Olie_Benzin_Stoffer) === ScreeningFlag.Kun_Olie_Benzin_Stoffer) {
            ret += ';' + '* Lokalitet er udelukkende forurenet med olie- og benzin stoffer\n';
        }

        const exportType = 'CSV';
        // addScreeningResults V2
        for (const resultKey of Object.keys(v2ScreeningResults)) {
            const num = parseFloat(resultKey);
            const title = `V2 - ${this.locationDetailsHelper.getNumberFormat(num)} m2 - Screeningsresultater`;

            fieldDefs.push({
                label: title,
                value: 'labelHeader'
            });
            ret += title + '\n';

            const values = this.getBodyText(v2ScreeningResults[resultKey], data, ret, exportType);
            data = values.data;
            ret = values.ret;

            fieldDefs.push({
                label: `'V2 table header`,
                value: 'tableHeaderLabel'
            });

            fieldDefs.push({
                label: `'V2 table body`,
                value: 'tableBodyText'
            });
        }

        // addScreeningResults V1
        for (const resultKey of Object.keys(v1ScreeningResults)) {
            const num = parseFloat(resultKey);
            const title = `V1 - ${this.locationDetailsHelper.getNumberFormat(num)} m2 - Screeningsresultater`;

            fieldDefs.push({
                label: title,
                value: 'labelHeader'
            });
            ret += title + '\n';

            const values = this.getBodyText(v1ScreeningResults[resultKey], data, ret, exportType);
            data = values.data;
            ret = values.ret;

            fieldDefs.push({
                label: `'V1 table header`,
                value: 'tableHeaderLabel'
            });

            fieldDefs.push({
                label: `'V1 table body`,
                value: 'tableBodyText'
            });
        }

        return {
            fieldDefs,
            data,
            ret
        };
    }

    private getBodyText(screeningResultsByKey: any, data: any, ret: any, exportType: string): any {
        // Group by CompoundTranslationType
        const activityScreeningResults = screeningResultsByKey
            .filter(el => el.compoundTranslationType === CompoundTranslationType.Activity);
        const pollutionCauseScreeningResults = screeningResultsByKey
            .filter(el => el.compoundTranslationType === CompoundTranslationType.PollutionCause);
        const pollutantScreeningResults = screeningResultsByKey
            .filter(el => el.compoundTranslationType === CompoundTranslationType.Pollutant);
        let body = [];

        // Stoffer
        if (pollutantScreeningResults && pollutantScreeningResults.length > 0) {
            const headers = this.locationDetailsHelper.getScreengResultTableHeader(CompoundTranslationTypeGroupKey.Stoffer);
            ret += 'Stoffer\n';
            data.tableHeaderLabel = headers.join(';');
            ret += headers.join(';') + '\n';

            for (const result of pollutantScreeningResults) {
                body.push(this.locationDetailsHelper.getRow(CompoundTranslationTypeGroupKey.Stoffer, result, exportType).join(';') + '\n');
            }
            data.tableBodyText = body.join(';');
            ret += body.join('');
            body = [];
        }

        // Brancher
        if (pollutionCauseScreeningResults && pollutionCauseScreeningResults.length > 0) {
            const headers = this.locationDetailsHelper.getScreengResultTableHeader(CompoundTranslationTypeGroupKey.Brancher);
            ret += 'Brancher\n';
            data.tableHeaderLabel = headers.join(';');
            ret += headers.join(';') + '\n';

            for (const result of pollutionCauseScreeningResults) {
                body.push(this.locationDetailsHelper.getRow(CompoundTranslationTypeGroupKey.Brancher, result, exportType).join(';') + '\n');
            }
            data.tableBodyText = body.join(';');
            ret += body.join('');
            body = [];
        }

        // Aktiviteter
        if (activityScreeningResults && activityScreeningResults.length > 0) {
            const headers = this.locationDetailsHelper.getScreengResultTableHeader(CompoundTranslationTypeGroupKey.Aktiviteter);
            ret += 'Aktiviteter\n';
            data.tableHeaderLabel = headers.join(';');
            ret += headers.join(';') + '\n';

            for (const result of activityScreeningResults) {
                body.push(this.locationDetailsHelper.getRow(CompoundTranslationTypeGroupKey.Aktiviteter, result, exportType).join(';') + '\n');
            }
            data.tableBodyText = body.join(';');
            ret += body.join('');
            body = [];
        }

        return { data, ret };
    }
}
